import { FC, useCallback } from "react";
import { DndProvider, useDrag, useDrop } from "react-dnd";
import { HTML5Backend } from "react-dnd-html5-backend";
import styled from "styled-components";

import { GalleryImage } from "./forms/UpdateLotForm/initialValues";

const ImageGalleryContainer = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 10px;
  margin: 15px 0 5px;
`;
const ImageItem = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
  object-position: center;
  display: block;
  cursor: grab;
`;

const ImageDeleteButton = styled.div`
  position: absolute;
  right: 0;
  top: 0;
  z-index: 1;
  cursor: pointer;
  line-height: 0.6;
  text-shadow: 1px 1px 2px black;
`;

const ImageItemContainer = styled.div`
  aspect-ratio: 1 / 1;
  overflow: hidden;
  position: relative;
  border-radius: 4px;
`;

const IMAGE_TYPE = "image";

interface IImageGallery {
  images: GalleryImage[];
  setImages: (images: GalleryImage[]) => void;
}

export const ImageGallery: FC<IImageGallery> = ({ images, setImages }) => {
  const [, drop] = useDrop(() => ({ accept: IMAGE_TYPE }));

  const handleDelete = (id: string) => {
    const updated = [...images].map((item) =>
      item.id === id ? { ...item, isDelete: true } : item
    );
    setImages(updated);
  };

  const findImage = useCallback(
    (url: string) => {
      const image = images.filter((c) => c.url === url)[0];
      return {
        image: image.url,
        index: images.findIndex((x) => x.url === image.url),
      };
    },
    [images]
  );

  const moveCard = useCallback(
    (url: string, atIndex: number) => {
      const { index } = findImage(url);
      const arr = [...images];
      const arrByIndex = images[index];
      arr.splice(index, 1);
      arr.splice(atIndex, 0, arrByIndex);
      setImages(arr);
    },
    [findImage]
  );

  return (
    <DndProvider backend={HTML5Backend}>
      <ImageGalleryContainer ref={drop}>
        {images
          .filter(({ isDelete }) => !isDelete)
          .map(({ url, id }, index) => (
            <Image
              item={url}
              index={index}
              key={id}
              onDelete={() => handleDelete(id)}
              moveCard={moveCard}
              findImage={findImage}
            />
          ))}
      </ImageGalleryContainer>
    </DndProvider>
  );
};

interface ImageProps {
  item: string;
  index: number;
  onDelete: () => void;
  moveCard: any;
  findImage: (url: string) => { image: string; index: number };
}

const Image: FC<ImageProps> = ({ item, onDelete, moveCard, findImage }) => {
  const originalIndex = findImage(item).index;

  const [{ isDragging }, drag] = useDrag(
    () => ({
      type: IMAGE_TYPE,
      item: { item, originalIndex },
      collect: (monitor) => ({
        isDragging: monitor.isDragging(),
      }),
      end: (item, monitor) => {
        const { item: image, originalIndex } = item;
        const didDrop = monitor.didDrop();
        if (!didDrop) {
          moveCard(image, originalIndex);
        }
      },
    }),
    [item, originalIndex, moveCard]
  );

  const [, drop] = useDrop(
    () => ({
      accept: IMAGE_TYPE,
      drop({ item: image }: { item: string }) {
        if (image !== item) {
          const { index: overIndex } = findImage(item);
          moveCard(image, overIndex);
        }
      },
    }),
    [moveCard, findImage]
  );

  const opacity = isDragging ? 0 : 1;

  return (
    <ImageItemContainer
      ref={(node) => drag(drop(node))}
      key={item}
      style={{ opacity }}
    >
      <ImageDeleteButton onClick={onDelete}>x</ImageDeleteButton>
      <ImageItem src={item} />
    </ImageItemContainer>
  );
};
